import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { acquireToken, fetchAdminPermissions, tokenRenewed } from '../adminSlice';
import { acquireTokenSilently, isSessionExpired, onTokenRefreshed } from './tokenRefresh';

// Renew this long before expiry, so a request in flight never carries a token
// that dies mid-way.
const REFRESH_MARGIN_MS = 5 * 60 * 1000;
// If a renewal fails (e.g. offline for a moment), try again after this.
const RETRY_MS = 60 * 1000;
// setTimeout overflows past ~24.8 days; tokens never live that long, but clamp.
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

// Keeps the Microsoft access token fresh for as long as the app is open, with
// no UI: a timer renews it shortly before expiry, and waking the tab / laptop
// (where timers get paused) triggers an immediate check.
export default function useSilentTokenRefresh() {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.admin?.token);
  const isAuthenticated = useSelector((state) => state.admin?.isAuthenticated);
  const expiresOn = useSelector((state) => state.admin?.tokenExpiresOn);
  const expiresOnRef = useRef(expiresOn);
  expiresOnRef.current = expiresOn;

  // Every renewal — timer, wake-up or a 401 retry — records its expiry, which
  // re-arms the timer below. Only the expiry changes, so no page refetches.
  useEffect(() => onTokenRefreshed((result) => dispatch(tokenRenewed(result))), [dispatch]);

  // Timer: renew REFRESH_MARGIN_MS before expiry. A new expiresOn re-arms it.
  useEffect(() => {
    if (!token || !expiresOn) return undefined;
    let cancelled = false;
    let timer = null;

    const renew = async () => {
      const result = await acquireTokenSilently({ forceRefresh: true });
      // A dead session won't come back by retrying; the next request shows the
      // "session expired" toast instead.
      if (!result && !cancelled && !isSessionExpired()) timer = setTimeout(renew, RETRY_MS);
    };

    const delay = Math.min(Math.max(expiresOn - Date.now() - REFRESH_MARGIN_MS, 0), MAX_TIMEOUT_MS);
    timer = setTimeout(renew, delay);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [token, expiresOn]);

  // The page-load attempt finished without a token (e.g. offline for a moment):
  // keep retrying quietly, and immediately when the tab or network comes back.
  // Permissions load once a token arrives, which lifts the loader.
  useEffect(() => {
    if (!isAuthenticated || token) return undefined;
    let busy = false;
    const retry = () => {
      if (busy || document.visibilityState === 'hidden') return;
      busy = true;
      dispatch(acquireToken())
        .unwrap()
        .then((result) => {
          if (result?.accessToken) dispatch(fetchAdminPermissions());
        })
        .catch(() => {})
        .finally(() => {
          busy = false;
        });
    };
    const timer = setInterval(retry, RETRY_MS / 2);
    window.addEventListener('focus', retry);
    window.addEventListener('online', retry);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', retry);
      window.removeEventListener('online', retry);
    };
  }, [dispatch, isAuthenticated, token]);

  // Wake-up: background tabs and sleeping laptops pause timers, so check again
  // whenever the user comes back or the network returns.
  useEffect(() => {
    if (!token) return undefined;
    const checkNow = () => {
      if (document.visibilityState === 'hidden') return;
      const exp = expiresOnRef.current;
      if (exp && exp - Date.now() < REFRESH_MARGIN_MS) {
        acquireTokenSilently({ forceRefresh: true });
      }
    };
    document.addEventListener('visibilitychange', checkNow);
    window.addEventListener('focus', checkNow);
    window.addEventListener('online', checkNow);
    return () => {
      document.removeEventListener('visibilitychange', checkNow);
      window.removeEventListener('focus', checkNow);
      window.removeEventListener('online', checkNow);
    };
  }, [token]);
}
